import { Head, Link, router } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { ArrowLeft, Upload, Download, CheckCircle, AlertCircle, XCircle } from 'lucide-react'
import { FormEvent, useRef, useState } from 'react'

interface PreviewRow {
  row_number: number
  first_name: string
  last_name: string
  email: string
  sponsor_email: string | null
  status: 'valid' | 'duplicate' | 'error'
  message: string | null
  contract_signed: string | null
  surancebay_completed: string | null
  ffr_registration_completed: string | null
}

interface ImportResult {
  file_name: string
  total_rows: number
  valid_count: number
  success_count: number
  skipped_count: number
  error_count: number
  rows: PreviewRow[]
}

interface Props extends PageProps {
  expected_columns: string[]
  required_columns: string[]
  preview: ImportResult | null
  results: ImportResult | null
}

const TEMPLATE_HEADERS = [
  'first_name',
  'last_name',
  'email',
  'sponsor_email',
  'phone',
  'npn_number',
  'override_percentage',
  'address_line1',
  'address_line2',
  'city',
  'state',
  'zip',
  'contract_signed',
  'surancebay_completed',
  'ffr_registration_completed'
]

const TEMPLATE_SAMPLE_ROWS = [
  ['Jane', 'Smith', 'jane@example.com', '', '555-1234', '1234567', '10', '100 Main St', '', 'Austin', 'TX', '78701', 'yes', '2024-03-15', 'yes'],
  ['John', 'Doe', 'john@example.com', 'jane@example.com', '555-5678', '7654321', '10', '', '', '', '', '', '', '', '']
]

function downloadTemplate() {
  const rows = [TEMPLATE_HEADERS, ...TEMPLATE_SAMPLE_ROWS]
  const csv = rows.map((r) => r.map((v) => {
    const s = String(v ?? '')
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }).join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'agent_import_template.csv'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export default function AgentsImport({ expected_columns, required_columns, preview, results }: Props) {
  const [file, setFile] = useState<File | null>(null)
  const [processing, setProcessing] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const submit = (confirm: boolean) => {
    if (!file) return
    setProcessing(true)
    const formData = new FormData()
    formData.append('file', file)
    if (confirm) formData.append('confirm', '1')
    router.post('/agents/upload', formData, {
      forceFormData: true,
      onFinish: () => setProcessing(false),
      preserveScroll: true
    })
  }

  const handlePreview = (e: FormEvent) => {
    e.preventDefault()
    submit(false)
  }

  const handleConfirm = () => {
    submit(true)
  }

  const handleStartOver = () => {
    setFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
    router.visit('/agents/import')
  }

  const statusBadge = (status: PreviewRow['status']) => {
    const classes: Record<PreviewRow['status'], string> = {
      valid: 'bg-green-100 text-green-700',
      duplicate: 'bg-yellow-100 text-yellow-700',
      error: 'bg-red-100 text-red-700'
    }
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${classes[status]}`}>
        {status}
      </span>
    )
  }

  const renderRowsTable = (rows: PreviewRow[]) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-chestnut-gray text-left text-chestnut-charcoal/70">
            <th className="py-2 pr-3">Row</th>
            <th className="py-2 pr-3">Name</th>
            <th className="py-2 pr-3">Email</th>
            <th className="py-2 pr-3">Sponsor</th>
            <th className="py-2 pr-3">Contract signed</th>
            <th className="py-2 pr-3">SuranceBay</th>
            <th className="py-2 pr-3">FFR registration</th>
            <th className="py-2 pr-3">Status</th>
            <th className="py-2 pr-3">Notes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.row_number} className="border-b border-chestnut-gray/40">
              <td className="py-2 pr-3 text-chestnut-charcoal/70">{r.row_number}</td>
              <td className="py-2 pr-3">{[r.first_name, r.last_name].filter(Boolean).join(' ') || '—'}</td>
              <td className="py-2 pr-3">{r.email || '—'}</td>
              <td className="py-2 pr-3 text-chestnut-charcoal/70">{r.sponsor_email || '—'}</td>
              <td className="py-2 pr-3 text-chestnut-charcoal/70">{r.contract_signed || '—'}</td>
              <td className="py-2 pr-3 text-chestnut-charcoal/70">{r.surancebay_completed || '—'}</td>
              <td className="py-2 pr-3 text-chestnut-charcoal/70">{r.ffr_registration_completed || '—'}</td>
              <td className="py-2 pr-3">{statusBadge(r.status)}</td>
              <td className="py-2 pr-3 text-chestnut-charcoal/70">{r.message || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  return (
    <AppLayout>
      <Head title="Import Agents" />

      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Link href="/agents" className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Import Agents</h1>
            <p className="text-chestnut-charcoal/70 mt-1">
              Bulk-create agent accounts from a spreadsheet. Nothing is sent to them.
            </p>
          </div>
        </div>

        {results ? (
          <div className="card p-6 space-y-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div>
                <h2 className="text-xl font-semibold text-chestnut-charcoal">Import complete</h2>
                <p className="text-sm text-chestnut-charcoal/70">File: {results.file_name}</p>
              </div>
            </div>

            <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 shrink-0 mt-0.5" />
              <p className="text-sm text-chestnut-charcoal/80">
                Accounts were created only — <strong>no email, login credentials, or contract was
                sent</strong>. Agents marked <code>contract_signed</code> are now <strong>Active</strong>;
                everyone else is a <strong>Prospect</strong>. Active agents still have no password — send
                them a welcome email before they can sign in.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="card border-l-4 border-l-green-500 p-4">
                <div className="text-xs uppercase tracking-wide text-chestnut-charcoal/60">Created</div>
                <div className="text-2xl font-bold text-chestnut-charcoal">{results.success_count}</div>
              </div>
              <div className="card border-l-4 border-l-yellow-500 p-4">
                <div className="text-xs uppercase tracking-wide text-chestnut-charcoal/60">Skipped (duplicates)</div>
                <div className="text-2xl font-bold text-chestnut-charcoal">{results.skipped_count}</div>
              </div>
              <div className="card border-l-4 border-l-red-500 p-4">
                <div className="text-xs uppercase tracking-wide text-chestnut-charcoal/60">Errors</div>
                <div className="text-2xl font-bold text-chestnut-charcoal">{results.error_count}</div>
              </div>
              <div className="card border-l-4 border-l-chestnut-forest p-4">
                <div className="text-xs uppercase tracking-wide text-chestnut-charcoal/60">Total rows</div>
                <div className="text-2xl font-bold text-chestnut-charcoal">{results.total_rows}</div>
              </div>
            </div>

            {results.skipped_count > 0 && (
              <div>
                <h3 className="font-semibold text-chestnut-charcoal mb-2 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-yellow-600" />
                  Skipped duplicates
                </h3>
                <ul className="text-sm text-chestnut-charcoal/80 list-disc pl-6">
                  {results.rows.filter((r) => r.status === 'duplicate').map((r) => (
                    <li key={r.row_number}>Row {r.row_number}: {r.email}</li>
                  ))}
                </ul>
              </div>
            )}

            {results.error_count > 0 && (
              <div>
                <h3 className="font-semibold text-chestnut-charcoal mb-2 flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-600" />
                  Errors
                </h3>
                <ul className="text-sm text-chestnut-charcoal/80 list-disc pl-6">
                  {results.rows.filter((r) => r.status === 'error').map((r) => (
                    <li key={r.row_number}>Row {r.row_number} ({r.email || '—'}): {r.message}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex gap-3 pt-4 border-t border-chestnut-gray">
              <Link href="/agents" className="btn-primary">Back to Agents</Link>
              <button type="button" onClick={handleStartOver} className="btn-secondary">
                Import another file
              </button>
            </div>
          </div>
        ) : preview ? (
          <div className="card p-6 space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-chestnut-charcoal">Preview: {preview.file_name}</h2>
              <p className="text-sm text-chestnut-charcoal/70">
                Review the parsed rows before confirming. Nothing has been saved yet.
              </p>
              <p className="text-sm text-chestnut-charcoal/70">
                Rows with <code>contract_signed</code> will be created <strong>Active</strong> (activated as of
                their contract date); the rest as <strong>Prospect</strong>. Either way no email, password, or
                contract is sent — active agents cannot sign in until you send a welcome email.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="card border-l-4 border-l-green-500 p-4">
                <div className="text-xs uppercase tracking-wide text-chestnut-charcoal/60">Valid</div>
                <div className="text-2xl font-bold text-chestnut-charcoal">{preview.valid_count}</div>
              </div>
              <div className="card border-l-4 border-l-yellow-500 p-4">
                <div className="text-xs uppercase tracking-wide text-chestnut-charcoal/60">Duplicates</div>
                <div className="text-2xl font-bold text-chestnut-charcoal">{preview.skipped_count}</div>
              </div>
              <div className="card border-l-4 border-l-red-500 p-4">
                <div className="text-xs uppercase tracking-wide text-chestnut-charcoal/60">Errors</div>
                <div className="text-2xl font-bold text-chestnut-charcoal">{preview.error_count}</div>
              </div>
              <div className="card border-l-4 border-l-chestnut-forest p-4">
                <div className="text-xs uppercase tracking-wide text-chestnut-charcoal/60">Total rows</div>
                <div className="text-2xl font-bold text-chestnut-charcoal">{preview.total_rows}</div>
              </div>
            </div>

            {renderRowsTable(preview.rows)}

            <div className="flex gap-3 pt-4 border-t border-chestnut-gray">
              <button
                type="button"
                onClick={handleConfirm}
                disabled={processing || preview.valid_count === 0}
                className="btn-primary"
              >
                {processing ? 'Importing...' : `Confirm & import ${preview.valid_count} agent${preview.valid_count === 1 ? '' : 's'}`}
              </button>
              <button type="button" onClick={handleStartOver} className="btn-secondary" disabled={processing}>
                Cancel / choose another file
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="card p-6 space-y-4">
              <h2 className="text-xl font-semibold text-chestnut-charcoal">Expected file format</h2>
              <p className="text-sm text-chestnut-charcoal/70">
                Upload a <code>.csv</code> or <code>.xlsx</code> file with the following columns. The first row must be the header.
              </p>
              <div>
                <h3 className="text-sm font-semibold text-chestnut-charcoal mb-2">Required columns</h3>
                <div className="flex flex-wrap gap-2">
                  {required_columns.map((c) => (
                    <span key={c} className="px-2 py-1 text-xs font-mono bg-chestnut-forest/10 text-chestnut-forest rounded">{c}</span>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-chestnut-charcoal mb-2">Optional columns</h3>
                <div className="flex flex-wrap gap-2">
                  {expected_columns.filter((c) => !required_columns.includes(c)).map((c) => (
                    <span key={c} className="px-2 py-1 text-xs font-mono bg-chestnut-gray/30 text-chestnut-charcoal rounded">{c}</span>
                  ))}
                </div>
              </div>
              <ul className="text-sm text-chestnut-charcoal/70 list-disc pl-6 space-y-1">
                <li>Leave <code>sponsor_email</code> blank for top-level (writing) agents.</li>
                <li>When <code>sponsor_email</code> matches an existing agent's email, the new agent is created as a sub-agent under that sponsor.</li>
                <li>Rows whose email already exists in your organization will be skipped as duplicates.</li>
                <li>Rows with an unknown <code>sponsor_email</code> will be flagged as errors.</li>
                <li>
                  <code>contract_signed</code>, <code>surancebay_completed</code> and{' '}
                  <code>ffr_registration_completed</code> record milestones the agent already finished.
                  Use <code>yes</code>/<code>no</code>, <code>true</code>/<code>false</code>,{' '}
                  <code>1</code>/<code>0</code>, <code>x</code>, or a completion date such as{' '}
                  <code>2024-03-15</code> to backdate it. Blank means not completed; anything else is a
                  row error.
                </li>
                <li>
                  Agents marked <code>contract_signed</code> are created as <strong>Active</strong>. They keep
                  their contract date, are activated as of that date, and are never sent an agreement to sign.
                </li>
                <li>
                  Importing <strong>only creates accounts</strong> — no email, login credentials, or
                  contract is sent. Rows without <code>contract_signed</code> are created as{' '}
                  <strong>Prospect</strong>. Active is a status, not a delivered login: an active imported
                  agent still has no password and cannot sign in until you send them a welcome email.
                </li>
              </ul>
              <button type="button" onClick={downloadTemplate} className="btn-secondary inline-flex items-center gap-2">
                <Download className="h-4 w-4" />
                Download CSV template
              </button>
            </div>

            <form onSubmit={handlePreview} className="card p-6 space-y-4">
              <h2 className="text-xl font-semibold text-chestnut-charcoal">Upload file</h2>
              <div>
                <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">
                  File (.csv or .xlsx)
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="input-field w-full"
                  required
                />
                {file && (
                  <p className="mt-1 text-xs text-chestnut-charcoal/60">Selected: {file.name}</p>
                )}
              </div>
              <div className="flex gap-3 pt-4 border-t border-chestnut-gray">
                <button type="submit" disabled={processing || !file} className="btn-primary inline-flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  {processing ? 'Parsing...' : 'Preview import'}
                </button>
                <Link href="/agents" className="btn-secondary">Cancel</Link>
              </div>
            </form>
          </>
        )}
      </div>
    </AppLayout>
  )
}
